/**
 * Shared citation formatting helpers for lawyer-grade exports (copy-with-citations,
 * DOCX export). Keeps the "Fuentes" line format identical across export surfaces:
 *
 *   [1] Oficio N° 1.212 (2023), SII — https://alertai.cl/documento/<id>
 *
 * Numbering always follows the citation's position in the `citations` array —
 * the same index CitationLink uses for its inline superscript — so a footnote
 * number in exported text always points at the same source as the in-app chip.
 */
import { Citation } from '../services/chatbot.service';
import { DOCUMENT_TYPE_LABELS } from './constants';

// 49 de 77 convenios traen número «0» (28-10): sin número real se usa el título («Convenio Chile-Estados Unidos…»).
const numeroReal = (n?: string | null): string | null => (n && n.trim() !== '0' ? n : null);

// Tipos del corpus que no están en DOCUMENT_TYPE_LABELS y no se escriben solo con mayúscula inicial.
const EXTRA_TIPO_LABELS: Record<string, string> = { dl: 'DL', dfl: 'DFL', ley: 'Ley', convenio: 'Convenio', jurisprudencia: 'Sentencia' };

/** "oficio" -> "Oficio", falls back to capitalizing unknown tipos. */
export function formatTipoLabel(tipo: string): string {
  const key = tipo?.toLowerCase();
  const known = (DOCUMENT_TYPE_LABELS as Record<string, string>)[key] || EXTRA_TIPO_LABELS[key];
  if (known) return known;
  if (!tipo) return '';
  return tipo.charAt(0).toUpperCase() + tipo.slice(1);
}

/** "Oficio N° 1.212 (2023), SII" — title built from titulo/tipo/numero/year/institution_code. */
export function buildCitationLabel(citation: Citation): string {
  const tipoLabel = formatTipoLabel(citation.tipo);
  const numeroPart = numeroReal(citation.numero) ? ` N° ${citation.numero}` : '';
  const yearPart = citation.year ? ` (${citation.year})` : '';
  const institutionPart = citation.institution_code ? `, ${citation.institution_code}` : '';

  const label = `${tipoLabel}${numeroPart}${yearPart}${institutionPart}`.trim();
  return label || citation.titulo || 'Documento';
}

/** Absolute link to the document detail page, built from the current origin
 *  (works across localhost/staging/prod without hardcoding a domain). */
export function buildDocumentUrl(documentId: string): string {
  const origin = typeof window !== 'undefined' && window.location ? window.location.origin : 'https://alertai.cl';
  return `${origin}/documento/${documentId}`;
}

/** "[1] Oficio N° 1.212 (2023), SII — https://alertai.cl/documento/<id>" */
export function buildFootnoteEntry(citation: Citation, index: number): string {
  return `[${index + 1}] ${buildCitationLabel(citation)} — ${buildDocumentUrl(citation.document_id)}`;
}

/** "2002-04-11" o ISO -> "11/04/2002" (sin pasar por Date, para no correr el día por la zona horaria). */
export function formatCitationDate(fecha?: string | null): string | null {
  const m = fecha?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : null;
}

/**
 * Fila de «Fuentes citadas» (M-8): "SII - Oficio N° 1187 (11/04/2002)". Las normas de la BCN van sin institución
 * ("Ley N° 21.210 (2020)"); sin número se usa el título. Fecha completa si viene; si no, el año.
 */
export function buildSourceLine(citation: Citation): string {
  const inst = citation.institution_code && citation.institution_code.toUpperCase() !== 'BCN' ? `${citation.institution_code} - ` : '';
  const tipo = formatTipoLabel(citation.tipo);
  const nombre = numeroReal(citation.numero) && tipo ? `${tipo} N° ${citation.numero}` : (citation.titulo || tipo || 'Documento');
  const cuando = formatCitationDate(citation.fecha) || (citation.year ? String(citation.year) : '');
  return `${inst}${nombre}${cuando ? ` (${cuando})` : ''}`;
}

/** Solo enlaces http(s): `source_url` viene de metadatos ingeridos y un `javascript:` no debe llegar a un href. */
export function safeExternalUrl(url?: string | null): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    return u.protocol === 'http:' || u.protocol === 'https:' ? u.href : null;
  } catch {
    return null;
  }
}
